import { useState, useRef, useEffect, useCallback } from "react"
import { Link, usePage } from "@inertiajs/react"
import { useCurrentUser } from "../../hooks/useCurrentUser"
import type { CurrentUser, SharedProps } from "./types"
import { Button } from "@/components/ui/button"

function getCsrfToken(): string {
  if (typeof document === "undefined") return ""
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") ?? ""
  )
}

// --- States Dropdown (Desktop flyout) ---

function StatesDropdown({ states }: { states: string[] }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false)
    }

    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("keydown", handleEscape)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleEscape)
    }
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <Button
        type="button"
        variant="ghost"
        size={null}
        className={`group bg-background rounded-md inline-flex items-center gap-0 text-base font-medium hover:bg-background hover:text-foreground-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand focus-visible:ring-2 focus-visible:ring-brand [&_svg]:size-5 ${
          open ? "text-foreground-primary" : "text-foreground-muted"
        }`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span>States</span>
        <svg
          className={`ml-2 h-5 w-5 group-hover:text-foreground-muted transition ease-in-out duration-150 ${
            open ? "text-foreground-subtle" : "text-foreground-faint"
          }`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </Button>

      {open && (
        <div className="absolute z-10 left-1/2 lg:left-[250%] transform -translate-x-1/2 mt-3 px-2 w-screen max-w-md sm:px-0 lg:max-w-3xl">
          <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden">
            <div className="relative grid gap-6 bg-card px-5 py-6 sm:gap-8 sm:p-8 lg:grid-cols-3">
              {states.map((state) => (
                <a
                  key={state}
                  href={`/directories/${state.toLowerCase().replace(/\s+/g, "-")}`}
                  className="-m-3 p-3 flex items-start rounded-lg hover:bg-surface-subtle transition ease-in-out duration-150 items-center"
                  onClick={() => setOpen(false)}
                >
                  <div className="flex-shrink-0 flex items-center justify-center h-5 w-5 rounded-md text-white sm:h-12 sm:w-12">
                    <img
                      src={`/images/flags/${state.toLowerCase().replace(/\s+/g, "-")}.png`}
                      className="border border-brand-strong"
                      alt={state}
                    />
                  </div>
                  <div className="ml-4">
                    <p className="text-base font-medium text-foreground-primary">
                      {state}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// --- Desktop User Menu ---

function DesktopUserMenu({
  currentUser,
  signOutPath,
  managedCoffeeShopSlug,
}: {
  currentUser: CurrentUser
  signOutPath: string
  managedCoffeeShopSlug: string | null
}) {
  // ponytail: reads the shared flag here and in MobileMenu instead of threading a prop
  const mcpEnabled = !!(usePage().props as unknown as SharedProps).mcp_enabled
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const ref = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Grouped into labeled sections; `section` changes trigger a divider
  // (+ label unless it's the unlabeled footer) in the render below.
  const menuItems: { path: string; name: string; section?: string }[] = [
    { path: `/u/${currentUser.username}`, name: "Your Profile" },
    { path: `/u/${currentUser.username}/edit`, name: "Your Account" },
    {
      path: `/u/${currentUser.username}/coffee-journal`,
      name: "My Coffee Journal",
      section: "Coffee",
    },
    {
      path: `/u/${currentUser.username}/coffee-beans`,
      name: "My Coffee Beans",
      section: "Coffee",
    },
    { path: "/my-orders", name: "My Orders", section: "Wallet" },
    { path: "/my-cards", name: "My Loyalty Cards", section: "Wallet" },
    { path: "/my-passes", name: "My Coffee Passes", section: "Wallet" },
    { path: "/my-qr", name: "My QR", section: "Wallet" },
    ...(managedCoffeeShopSlug
      ? [
          { path: "/scan", name: "Scan QR", section: "Business" },
          { path: "/manage", name: "Manage", section: "Business" },
        ]
      : []),
    {
      path: "/features/untung-ke-rugi",
      name: "Untung ke rugi?",
      section: "Business",
    },
    ...(mcpEnabled
      ? [{ path: "/features/ask-ai", name: "Ask AI", section: "Business" }]
      : []),
    { path: "/auctions", name: "Ads Auctions", section: "Business" },
    { path: "__sign_out__", name: "Sign out", section: "__footer__" },
  ]

  useEffect(() => {
    if (!open) return

    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("keydown", handleEscape)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleEscape)
    }
  }, [open])

  useEffect(() => {
    if (open) {
      setActiveIndex(-1)
      menuRef.current?.focus()
    }
  }, [open])

  function handleSignOut() {
    fetch(signOutPath, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": getCsrfToken(),
        Accept: "text/html",
      },
      credentials: "same-origin",
    }).then(() => {
      window.location.href = "/"
    })
  }

  function handleItemClick(item: (typeof menuItems)[number]) {
    setOpen(false)
    if (item.path === "__sign_out__") {
      handleSignOut()
    } else {
      window.location.href = item.path
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIndex((prev) => (prev < menuItems.length - 1 ? prev + 1 : prev))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev))
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault()
      handleItemClick(menuItems[activeIndex])
    } else if (e.key === "Tab") {
      setOpen(false)
    }
  }

  return (
    <div className="ml-3 relative" ref={ref}>
      <button
        type="button"
        className="flex text-sm rounded-full focus:outline-none"
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {currentUser.avatar_url ? (
          <img
            src={currentUser.avatar_url}
            className="h-10 w-10 rounded-full border border-brand"
            alt={currentUser.username}
          />
        ) : (
          <span className="h-8 w-8 rounded-full">
            <svg
              className="text-brand group-hover:text-brand flex-shrink-0 -ml-1 mr-3 h-8 w-8"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </span>
        )}
      </button>

      {open && (
        <div
          ref={menuRef}
          className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-card ring-1 ring-black ring-opacity-5 focus:outline-none"
          tabIndex={-1}
          onKeyDown={handleKeyDown}
        >
          {menuItems.map((item, i) => {
            const startsSection =
              i > 0 && item.section !== menuItems[i - 1].section
            return (
              <div key={item.path}>
                {startsSection && (
                  <>
                    <div className="my-1 border-t border-border-subtle" />
                    {item.section !== "__footer__" && (
                      <p className="px-4 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-foreground-faint">
                        {item.section}
                      </p>
                    )}
                  </>
                )}
                <a
                  href={item.path === "__sign_out__" ? "#" : item.path}
                  className={`block px-4 py-2 text-sm text-foreground-secondary ${
                    activeIndex === i ? "bg-surface-muted" : ""
                  }`}
                  role="menuitem"
                  tabIndex={-1}
                  onMouseEnter={() => setActiveIndex(i)}
                  onMouseLeave={() => setActiveIndex(-1)}
                  onClick={(e) => {
                    e.preventDefault()
                    handleItemClick(item)
                  }}
                >
                  {item.name}
                </a>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// --- Mobile Menu ---

function MobileMenu({
  open,
  onClose,
  states,
  currentUser,
  signInPath,
  signUpPath,
  signOutPath,
  isStaff,
  managedCoffeeShopSlug,
  currentPath,
  isLoading,
}: {
  open: boolean
  onClose: () => void
  states: string[]
  currentUser: CurrentUser | null
  signInPath: string
  signUpPath: string
  signOutPath: string
  isStaff: boolean
  managedCoffeeShopSlug: string | null
  currentPath: string
  isLoading: boolean
}) {
  const mcpEnabled = !!(usePage().props as unknown as SharedProps).mcp_enabled
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) {
      setUserMenuOpen(false)
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    function handleClickOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") onClose()
    }

    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("keydown", handleEscape)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleEscape)
    }
  }, [open, onClose])

  function handleSignOut() {
    fetch(signOutPath, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": getCsrfToken(),
        Accept: "text/html",
      },
      credentials: "same-origin",
    }).then(() => {
      window.location.href = "/"
    })
  }

  return (
    <div
      className={`absolute z-30 top-0 inset-x-0 p-2 transition transform origin-top-right md:hidden ${open ? "" : "hidden"}`}
    >
      <div
        ref={panelRef}
        // eslint-disable-next-line no-restricted-syntax -- ring-opacity-5 line, no gray-50 border role
        className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 bg-card divide-y-2 divide-gray-50"
      >
        <div className="pt-5 pb-6 px-5 sm:pb-8">
          <div className="flex items-center justify-between">
            <div>
              <a href="/" className="flex">
                <img src="/logo.svg" className="h-8 w-auto" alt="petakopi" />
              </a>
            </div>
            <div className="-mr-2">
              <Button
                type="button"
                variant="ghost"
                size={null}
                className="bg-background rounded-md p-2 inline-flex items-center justify-center text-foreground-faint hover:text-foreground-muted hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand focus-visible:ring-2 focus-visible:ring-brand [&_svg]:size-6"
                onClick={onClose}
              >
                <span className="sr-only">Close menu</span>
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </Button>
            </div>
          </div>
        </div>

        <div className="py-6 px-5">
          <div className="grid grid-cols-1 gap-4">
            <a
              href="/about"
              className={`rounded-md text-base font-medium hover:text-foreground-primary ${
                currentPath === "/about"
                  ? "text-foreground-primary"
                  : "text-foreground-muted"
              }`}
              onClick={onClose}
            >
              About
            </a>
            {/* Mobile States Dropdown */}
            <MobileStatesDropdown states={states} />

            <a
              href="/coffee-beans"
              className={`rounded-md text-base font-medium hover:text-foreground-primary ${
                currentPath.startsWith("/coffee-beans")
                  ? "text-foreground-primary"
                  : "text-foreground-muted"
              }`}
              onClick={onClose}
            >
              Coffee Beans
            </a>
            <a
              href="/events"
              className={`rounded-md text-base font-medium hover:text-foreground-primary ${
                currentPath.startsWith("/events")
                  ? "text-foreground-primary"
                  : "text-foreground-muted"
              }`}
              onClick={onClose}
            >
              Events
            </a>
            <a
              href="/new"
              className={`rounded-md text-base font-medium hover:text-foreground-primary ${
                currentPath === "/new" ? "text-brand-ink" : "text-brand-strong"
              }`}
              onClick={onClose}
            >
              Submit New
            </a>
            <a
              href="/trip-planner"
              className={`rounded-md text-base font-medium hover:text-foreground-primary ${
                currentPath === "/trip-planner"
                  ? "text-foreground-primary"
                  : "text-foreground-muted"
              }`}
              onClick={onClose}
            >
              Trip Planner
            </a>
            {managedCoffeeShopSlug && (
              <a
                href="/manage"
                className={`rounded-md text-base font-medium hover:text-foreground-primary ${
                  currentPath.startsWith("/manage")
                    ? "text-foreground-primary"
                    : "text-foreground-muted"
                }`}
                onClick={onClose}
              >
                Manage
              </a>
            )}
            {isStaff && (
              <a
                href="/admin"
                className="rounded-md text-base font-medium text-red-500 hover:text-red-900"
                onClick={onClose}
              >
                Admin
              </a>
            )}
          </div>

          {isLoading ? null : currentUser ? (
            <div className="mt-3 relative">
              <button
                type="button"
                className="group bg-background rounded-md inline-flex items-center text-base font-medium hover:text-foreground-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand text-foreground-primary"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                aria-expanded={userMenuOpen}
              >
                {currentUser.avatar_url ? (
                  <img
                    src={currentUser.avatar_url}
                    className="h-10 w-10 rounded-full border border-brand"
                    alt={currentUser.username}
                  />
                ) : (
                  <span className="h-8 w-8 rounded-full">
                    <svg
                      className="text-brand group-hover:text-brand flex-shrink-0 -ml-1 mr-3 h-8 w-8"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </span>
                )}
                <svg
                  className={`ml-2 h-5 w-5 group-hover:text-foreground-muted transition ease-in-out duration-150 ${
                    userMenuOpen
                      ? "text-foreground-subtle"
                      : "text-foreground-faint"
                  }`}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {userMenuOpen && (
                <div className="absolute z-10 left-1/2 transform -translate-x-1/2 mt-3 px-2 w-screen max-w-md sm:px-0 lg:max-w-3xl">
                  <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden">
                    <div className="relative bg-card grid grid-cols-1 gap-4 px-5 py-6">
                      <a
                        href={`/u/${currentUser.username}`}
                        className="rounded-md text-base font-medium text-foreground-muted hover:text-foreground-primary"
                        onClick={onClose}
                      >
                        Your Profile
                      </a>
                      <a
                        href={`/u/${currentUser.username}/edit`}
                        className="rounded-md text-base font-medium text-foreground-muted hover:text-foreground-primary"
                        onClick={onClose}
                      >
                        Your Account
                      </a>
                      <p className="border-t border-border-subtle pt-3 text-xs font-semibold uppercase tracking-wider text-foreground-faint">
                        Coffee
                      </p>
                      <a
                        href={`/u/${currentUser.username}/coffee-journal`}
                        className="rounded-md text-base font-medium text-foreground-muted hover:text-foreground-primary"
                        onClick={onClose}
                      >
                        My Coffee Journal
                      </a>
                      <a
                        href={`/u/${currentUser.username}/coffee-beans`}
                        className="rounded-md text-base font-medium text-foreground-muted hover:text-foreground-primary"
                        onClick={onClose}
                      >
                        My Coffee Beans
                      </a>
                      <p className="border-t border-border-subtle pt-3 text-xs font-semibold uppercase tracking-wider text-foreground-faint">
                        Wallet
                      </p>
                      <a
                        href="/my-orders"
                        className="rounded-md text-base font-medium text-foreground-muted hover:text-foreground-primary"
                        onClick={onClose}
                      >
                        My Orders
                      </a>
                      <a
                        href="/my-cards"
                        className="rounded-md text-base font-medium text-foreground-muted hover:text-foreground-primary"
                        onClick={onClose}
                      >
                        My Loyalty Cards
                      </a>
                      <a
                        href="/my-passes"
                        className="rounded-md text-base font-medium text-foreground-muted hover:text-foreground-primary"
                        onClick={onClose}
                      >
                        My Coffee Passes
                      </a>
                      <a
                        href="/my-qr"
                        className="rounded-md text-base font-medium text-foreground-muted hover:text-foreground-primary"
                        onClick={onClose}
                      >
                        My QR
                      </a>
                      <p className="border-t border-border-subtle pt-3 text-xs font-semibold uppercase tracking-wider text-foreground-faint">
                        Business
                      </p>
                      {managedCoffeeShopSlug && (
                        <a
                          href="/scan"
                          className="rounded-md text-base font-medium text-foreground-muted hover:text-foreground-primary"
                          onClick={onClose}
                        >
                          Scan QR
                        </a>
                      )}
                      {managedCoffeeShopSlug && (
                        <a
                          href="/manage"
                          className="rounded-md text-base font-medium text-foreground-muted hover:text-foreground-primary"
                          onClick={onClose}
                        >
                          Manage
                        </a>
                      )}
                      <a
                        href="/features/untung-ke-rugi"
                        className="rounded-md text-base font-medium text-foreground-muted hover:text-foreground-primary"
                        onClick={onClose}
                      >
                        Untung ke rugi?
                      </a>
                      {mcpEnabled && (
                        <a
                          href="/features/ask-ai"
                          className="rounded-md text-base font-medium text-foreground-muted hover:text-foreground-primary"
                          onClick={onClose}
                        >
                          Ask AI
                        </a>
                      )}
                      <a
                        href="/auctions"
                        className="rounded-md text-base font-medium text-foreground-muted hover:text-foreground-primary"
                        onClick={onClose}
                      >
                        Ads Auctions
                      </a>
                      <div className="border-t border-border-subtle" />
                      <a
                        href="#"
                        className="rounded-md text-base font-medium text-foreground-muted hover:text-foreground-primary"
                        onClick={(e) => {
                          e.preventDefault()
                          onClose()
                          handleSignOut()
                        }}
                      >
                        Sign Out
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-6">
              <a
                href={signUpPath}
                className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-brand-foreground bg-brand-strong hover:bg-brand-ink"
                onClick={onClose}
              >
                Sign up
              </a>
              <p className="mt-6 text-center text-base font-medium text-foreground-muted">
                Existing user?{" "}
                <a
                  href={signInPath}
                  className="text-brand-strong hover:text-brand"
                  onClick={onClose}
                >
                  Sign in
                </a>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// --- Mobile States Dropdown (within mobile menu) ---

function MobileStatesDropdown({ states }: { states: string[] }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  return (
    <div className="relative" ref={ref}>
      <Button
        type="button"
        variant="ghost"
        size={null}
        className={`group bg-background rounded-md inline-flex items-center gap-0 text-base font-medium hover:bg-background hover:text-foreground-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand focus-visible:ring-2 focus-visible:ring-brand [&_svg]:size-5 ${
          open ? "text-foreground-primary" : "text-foreground-muted"
        }`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span>States</span>
        <svg
          className={`ml-2 h-5 w-5 group-hover:text-foreground-muted transition ease-in-out duration-150 ${
            open ? "text-foreground-subtle" : "text-foreground-faint"
          }`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </Button>

      {open && (
        <div className="mt-3 px-2 w-full">
          <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden">
            <div className="relative grid gap-6 bg-card px-5 py-6 sm:gap-8 sm:p-8 lg:grid-cols-3">
              {states.map((state) => (
                <a
                  key={state}
                  href={`/directories/${state.toLowerCase().replace(/\s+/g, "-")}`}
                  className="-m-3 p-3 flex items-start rounded-lg hover:bg-surface-subtle transition ease-in-out duration-150 items-center"
                >
                  <div className="flex-shrink-0 flex items-center justify-center h-5 w-5 rounded-md text-white sm:h-12 sm:w-12">
                    <img
                      src={`/images/flags/${state.toLowerCase().replace(/\s+/g, "-")}.png`}
                      className="border border-brand-strong"
                      alt={state}
                    />
                  </div>
                  <div className="ml-4">
                    <p className="text-base font-medium text-foreground-primary">
                      {state}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// --- Main Header Component ---

export function Header({ hidden = false }: { hidden?: boolean }) {
  const page = usePage()
  const { sign_in_path, sign_up_path, sign_out_path, directory_states } =
    page.props as unknown as SharedProps
  const url = page.url

  const { data: meData, isLoading: meLoading } = useCurrentUser()
  const current_user = meData?.current_user ?? null
  const staff = meData?.staff ?? false
  const managed_coffee_shop_slug = meData?.managed_coffee_shop_slug ?? null

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  // Use Inertia's page.url for the initial render to avoid SSR/client hydration mismatch.
  // Strip query string to get just the pathname.
  const currentPath = url.split("?")[0]

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false)
  }, [])

  if (hidden) return null

  return (
    <div className="relative bg-background">
      <div
        className="absolute inset-0 shadow z-20 pointer-events-none"
        aria-hidden="true"
      />
      <div className="relative z-20">
        <div className="max-w-7xl mx-auto flex justify-between items-center px-4 py-5 sm:px-6 sm:py-4 lg:px-8 md:justify-start md:space-x-8">
          {/* Logo */}
          <div>
            <Link href="/" className="flex">
              <img
                src="/logo.svg"
                className="h-8 w-auto sm:h-10"
                alt="petakopi"
                width="40"
                height="40"
              />
            </Link>
          </div>

          {/* Mobile actions: My QR shortcut (logged in) + hamburger */}
          <div className="-mr-2 -my-2 md:hidden flex items-center">
            {!meLoading && current_user && (
              <a
                href="/my-qr"
                aria-label="My QR"
                className="rounded-md p-2 inline-flex items-center justify-center text-foreground-faint hover:text-foreground-muted hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand"
              >
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect width="5" height="5" x="3" y="3" rx="1" />
                  <rect width="5" height="5" x="16" y="3" rx="1" />
                  <rect width="5" height="5" x="3" y="16" rx="1" />
                  <path d="M21 16h-3a2 2 0 0 0-2 2v3" />
                  <path d="M21 21v.01" />
                  <path d="M12 7v3a2 2 0 0 1-2 2H7" />
                  <path d="M3 12h.01" />
                  <path d="M12 3h.01" />
                  <path d="M12 16v.01" />
                  <path d="M16 12h1" />
                  <path d="M21 12v.01" />
                  <path d="M12 21v-1" />
                </svg>
              </a>
            )}
            <Button
              type="button"
              variant="ghost"
              size={null}
              className="bg-background rounded-md p-2 inline-flex items-center justify-center text-foreground-faint hover:text-foreground-muted hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand focus-visible:ring-2 focus-visible:ring-brand [&_svg]:size-6"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
            >
              <span className="sr-only">Open menu</span>
              <svg
                className="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </Button>
          </div>

          {/* Desktop nav */}
          <div className="hidden md:flex-1 md:flex md:items-center md:justify-between">
            <nav className="flex space-x-10">
              <a
                href="/about"
                className={`text-base font-medium hover:text-foreground-primary flex items-center ${
                  currentPath === "/about"
                    ? "text-foreground-primary"
                    : "text-foreground-muted"
                }`}
              >
                About
              </a>

              <StatesDropdown states={directory_states} />

              <a
                href="/coffee-beans"
                className={`text-base font-medium hover:text-foreground-primary ${
                  currentPath.startsWith("/coffee-beans")
                    ? "text-foreground-primary"
                    : "text-foreground-muted"
                }`}
              >
                Coffee Beans
              </a>

              <a
                href="/events"
                className={`text-base font-medium hover:text-foreground-primary ${
                  currentPath.startsWith("/events")
                    ? "text-foreground-primary"
                    : "text-foreground-muted"
                }`}
              >
                Events
              </a>

              <a
                href="/new"
                className={`text-base font-medium hover:text-foreground-primary ${
                  currentPath === "/new"
                    ? "text-brand-ink"
                    : "text-brand-strong"
                }`}
              >
                Submit New
              </a>

              <a
                href="/trip-planner"
                className={`text-base font-medium hover:text-foreground-primary ${
                  currentPath === "/trip-planner"
                    ? "text-foreground-primary"
                    : "text-foreground-muted"
                }`}
              >
                Trip Planner
              </a>

              {managed_coffee_shop_slug && (
                <a
                  href="/manage"
                  className={`text-base font-medium hover:text-foreground-primary ${
                    currentPath.startsWith("/manage")
                      ? "text-foreground-primary"
                      : "text-foreground-muted"
                  }`}
                >
                  Manage
                </a>
              )}

              {staff && (
                <a
                  href="/admin"
                  className="text-base font-medium text-red-500 hover:text-red-900"
                >
                  Admin
                </a>
              )}
            </nav>

            {meLoading ? (
              <div className="ml-3">
                <div className="h-10 w-10 rounded-full bg-surface-strong animate-pulse" />
              </div>
            ) : current_user ? (
              <DesktopUserMenu
                currentUser={current_user}
                signOutPath={sign_out_path}
                managedCoffeeShopSlug={managed_coffee_shop_slug}
              />
            ) : (
              <div className="flex items-center md:ml-12">
                <a
                  href={sign_in_path}
                  className="text-base font-medium text-foreground-muted hover:text-foreground-primary"
                >
                  Sign in
                </a>
                <a
                  href={sign_up_path}
                  className="ml-8 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-brand-foreground bg-brand-strong hover:bg-brand-ink"
                >
                  Sign up
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      <MobileMenu
        open={mobileMenuOpen}
        onClose={closeMobileMenu}
        states={directory_states}
        currentUser={current_user}
        signInPath={sign_in_path}
        signUpPath={sign_up_path}
        signOutPath={sign_out_path}
        isStaff={staff}
        managedCoffeeShopSlug={managed_coffee_shop_slug}
        currentPath={currentPath}
        isLoading={meLoading}
      />
    </div>
  )
}
